import React, { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePdfExportInputs } from '../hooks/usePdfExportInputs';
import { getNorms } from '../store/selectors/user.selector';
import { type LuminairePdfDisplay, resolveLuminairePdfDisplayByUuid } from '../utils/pdfLuminaireDisplay';
import { createLayerTitle, getIlluminatedLayers } from '../utils/pdfUtils';
import { useFrontendReportImages } from './FrontendReportImagesContext';
import { type PdfCacheProps, usePdfCache } from './PdfCacheContext';
import { useExport } from './useExport';

/* eslint-disable react-refresh/only-export-components */

interface PdfExportContextValue {
  pdfUrl: string | null;
  /** True while plots or PDF assembly are in progress and no URL is available yet. */
  isExportLoading: boolean;
  isGeneratingPdf: boolean;
  isGeneratingPlots: boolean;
}

const PdfExportContext = createContext<PdfExportContextValue>({
  pdfUrl: null,
  isExportLoading: false,
  isGeneratingPdf: false,
  isGeneratingPlots: false,
});

function buildPdfProps(
  checkedItems: PdfCacheProps['checkedItems'],
  selectedMeasurements: PdfCacheProps['checkedMeasurementsItems'],
  project: PdfCacheProps['project'] | null | undefined,
  activeVersion: PdfCacheProps['activeVersion'] | null | undefined,
  measurementData: PdfCacheProps['measurementData'] | null,
  reportImageUrls: PdfCacheProps['reportImageUrls'],
  unitSystem: PdfCacheProps['unitSystem'],
  norms: PdfCacheProps['norms'],
  frontendReportImageUrls: Record<string, string> | undefined,
  luminaireDisplayByUuid: Record<string, LuminairePdfDisplay>,
  selectedPhotometries: PdfCacheProps['selectedPhotometries'],
): PdfCacheProps | null {
  if (!project || !activeVersion || !measurementData) return null;

  return {
    checkedItems,
    checkedMeasurementsItems: selectedMeasurements,
    project,
    activeVersion,
    norms,
    reportImageUrls,
    frontendReportImageUrls,
    luminaireDisplayByUuid,
    measurementData,
    unitSystem,
    selectedPhotometries,
  };
}

export const PdfExportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { checkedItems, selectedMeasurements, selectedPhotometries } = useExport();
  const { getCachedPdf, isGenerating: isGeneratingPdf } = usePdfCache();
  const {
    images: frontendReportImageUrls,
    isGenerating: isGeneratingPlots,
    generationComplete: plotsGenerationComplete,
    useFrontendPlots,
  } = useFrontendReportImages();
  const { project, activeVersion, measurementData, reportImageUrls, unitSystem, pdfExportEnabled } =
    usePdfExportInputs();
  const norms = useSelector(getNorms);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [luminaireDisplayByUuid, setLuminaireDisplayByUuid] = useState<Record<string, LuminairePdfDisplay>>({});
  const [luminaireDisplayReady, setLuminaireDisplayReady] = useState(false);
  const resolvedLuminaireSignatureRef = useRef('');

  const luminaireSignature = useMemo(
    () =>
      (activeVersion?.lights ?? [])
        .map((l) => `${l.uuid}:${l.productId}:${l.variantId}:${l.manufacturerId}:${l.name}:${l.lumenOutput ?? ''}`)
        .join('|'),
    [activeVersion?.lights],
  );

  useEffect(() => {
    const lights = activeVersion?.lights;
    if (!lights?.length) {
      setLuminaireDisplayByUuid({});
      setLuminaireDisplayReady(true);
      resolvedLuminaireSignatureRef.current = '';
      return;
    }

    if (resolvedLuminaireSignatureRef.current === luminaireSignature) {
      return;
    }

    let cancelled = false;
    setLuminaireDisplayReady(false);

    void resolveLuminairePdfDisplayByUuid(lights).then((resolved) => {
      if (!cancelled) {
        setLuminaireDisplayByUuid(resolved);
        setLuminaireDisplayReady(true);
        resolvedLuminaireSignatureRef.current = luminaireSignature;
      }
    });

    return () => {
      cancelled = true;
    };
    // luminaireSignature captures activeVersion.lights catalogue fields
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVersion?.id, luminaireSignature]);

  const effectiveSelectedMeasurements = useMemo(() => {
    if (Object.keys(selectedMeasurements).length > 0) {
      return selectedMeasurements;
    }
    if (!activeVersion?.meshes || norms.length === 0) {
      return selectedMeasurements;
    }
    const defaults: Record<string, boolean> = {};
    getIlluminatedLayers(activeVersion).forEach((mesh) => {
      const layerTitle = createLayerTitle(
        mesh.displayName || mesh.name,
        mesh.normId,
        norms,
        mesh.targets?.length ? 'custom' : undefined,
      );
      defaults[layerTitle] = true;
    });
    return defaults;
  }, [selectedMeasurements, activeVersion, norms]);

  useEffect(() => {
    if (!pdfExportEnabled) {
      setPdfUrl(null);
      return;
    }

    if (!luminaireDisplayReady) {
      return;
    }

    // Frontend PDF plots must finish before assembly — no partial export.
    if (useFrontendPlots && (isGeneratingPlots || !plotsGenerationComplete)) {
      setPdfUrl(null);
      return;
    }

    const props = buildPdfProps(
      checkedItems,
      effectiveSelectedMeasurements,
      project,
      activeVersion,
      measurementData,
      reportImageUrls,
      unitSystem,
      norms,
      useFrontendPlots ? frontendReportImageUrls : undefined,
      luminaireDisplayByUuid,
      selectedPhotometries,
    );
    if (!props) {
      setPdfUrl(null);
      return;
    }

    let cancelled = false;

    void getCachedPdf(props)
      .then((url) => {
        if (!cancelled) setPdfUrl(url);
      })
      .catch((error) => {
        console.error('PdfExportProvider: PDF generation failed:', error);
        if (!cancelled) setPdfUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    pdfExportEnabled,
    project,
    activeVersion,
    measurementData,
    reportImageUrls,
    unitSystem,
    checkedItems,
    effectiveSelectedMeasurements,
    selectedPhotometries,
    norms,
    useFrontendPlots,
    isGeneratingPlots,
    plotsGenerationComplete,
    frontendReportImageUrls,
    luminaireDisplayByUuid,
    luminaireDisplayReady,
    getCachedPdf,
  ]);

  const value = useMemo(
    () => ({
      pdfUrl,
      isExportLoading:
        ((!measurementData || !luminaireDisplayReady) && pdfExportEnabled && !pdfUrl) ||
        (useFrontendPlots && (isGeneratingPlots || !plotsGenerationComplete) && pdfExportEnabled && !pdfUrl) ||
        (isGeneratingPdf && !pdfUrl),
      isGeneratingPdf,
      isGeneratingPlots,
    }),
    [
      pdfUrl,
      isGeneratingPdf,
      isGeneratingPlots,
      luminaireDisplayReady,
      measurementData,
      pdfExportEnabled,
      useFrontendPlots,
      plotsGenerationComplete,
    ],
  );

  return <PdfExportContext.Provider value={value}>{children}</PdfExportContext.Provider>;
};

export function usePdfExport(): PdfExportContextValue {
  return useContext(PdfExportContext);
}
